<script setup lang="ts">
import { Tippy } from 'vue-tippy'
import request from 'axios'
import { v4 } from 'uuid'
import type { CSSProperties } from 'vue'
import {
  type CreatorProfileByCreatorLinkPayload,
  type CreatorProfileEditPayload,
  type ProfilePayload,
  LayerType,
} from '@store'
import {
  NovaModalCommon,
  NovaModalDialog,
  NovaModalRetireCheckPassword,
} from '#components'
import { useCheckEmpty } from '@composables/useCheckEmpty'
import { ROUTES } from '@configs'

const route = useRoute()
const { gtEvent } = useGoogleTag()
const { t } = useI18n()
const { show: modalShow, hide: modalHide } = useModal()
const { checkPermission, requestPermission } = useAppPermission()
const { certifiedKYC } = useKyc()
const { openMembershipServicePopup } = useMembershipProcess()

const userStore = useUserStore()
const { user, isCreator } = storeToRefs(userStore)
const creatorStore = useCreatorStore()
const mypageStore = useMyPageStore()
const layoutStore = useLayoutStore()
const { responsive, bottomTabBarRect } = storeToRefs(layoutStore)
const appStore = useAppStore()

const styles = computed(() => {
  const mobile: CSSProperties = {
    height: `calc(100% - ${useGetStyleSize(
      bottomTabBarRect.value?.height || 0
    )})`,
  }
  return responsive.value === 'mobile' ? mobile : {}
})
const isOpen = computed(() =>
  layoutStore.layers.includes(LayerType.USER_PROFILE_EDIT_PANEL)
)
const isReady = ref(user.value?.cmtyUserSeCode !== 'C')
const state = reactive({
  userNcnm: user.value?.userNcnm || '',
  proflDc: user.value?.proflDc || '',
  nameMaxLength: 25,
  proflDcMaxLength: 30,
  createLinkInput: user.value?.creatorLink || '',
  optionNcmInput: '',
  optionCharstValInput: '',
  isNickname: false,
  isSimpleIntroduce: false,
})
const countLengthByUserNcnm = computed(() => state.userNcnm.length)
const counteLengthByProflDc = computed(() => state.proflDc.length)
const kycCertified = computed(() => user.value?.kycAt === 'Y')
const isBgImgLoadSuccess = ref(false)

const initProfile = useDebounce(async () => {
  if (user.value?.cmtyUserSeCode === 'C') await fetchMyProfile()
  state.userNcnm = user.value?.userNcnm || ''
}, 1000)

watch(
  () => isOpen.value,
  (cur) => {
    if (cur) {
      initProfile()
    }
  }
)

watch(
  () => state.userNcnm,
  (_cur) => {
    if (countLengthByUserNcnm.value > state.nameMaxLength) {
      state.userNcnm = _cur.slice(0, state.nameMaxLength)
    }
  }
)

watch(
  () => state.proflDc,
  (_cur) => {
    if (counteLengthByProflDc.value > state.proflDcMaxLength) {
      state.proflDc = state.proflDc.slice(0, state.proflDcMaxLength)
    }
  }
)

const fetchMyProfile = async () => {
  try {
    isReady.value = false
    if (creatorStore.myProfile?.userSn !== user.value?.userSn) {
      await creatorStore.fetchMyProfile({
        creatorLink: user.value!.creatorLink,
      })
    }
    if (creatorStore.profile?.userSn === user.value?.userSn) {
      await creatorStore.fetchCreatorHome({
        creatorUserSn: user.value!.userSn,
      })
    }
  } catch {
  } finally {
    isReady.value = true
  }
}

const handleOnChangeUserNcnm = () => {
  state.isNickname = true
  state.userNcnm = user.value?.userNcnm || ''
  gtEvent('clickEvent', {
    eventCategory: '클릭',
    eventAction: '닉네임 변경 버튼 클릭',
    eventLabel: t('modify'),
    eventSlot: '프로필 수정 > 닉네임',
    eventI18nAddr: useKoreanTranslation('modify'),
    eventComponent: 'Button',
  })
}

const handleOnChangeSimpleIntroduce = () => {
  state.isSimpleIntroduce = true
  state.proflDc = user.value?.proflDc || ''
  gtEvent('clickEvent', {
    eventCategory: '클릭',
    eventAction: '한줄소개 변경 버튼 클릭',
    eventLabel: t('modify'),
    eventSlot: '프로필 수정 > 한줄소개',
    eventI18nAddr: useKoreanTranslation('modify'),
    eventComponent: 'Button',
  })
}

const onCancelName = () => {
  state.userNcnm = ''
  state.isNickname = false
  gtEvent('clickEvent', {
    eventCategory: '클릭',
    eventAction: '닉네임 변경 취소',
    eventLabel: t('cancel'),
    eventSlot: '프로필 수정 > 닉네임 변경 > 취소',
    eventI18nAddr: useKoreanTranslation('cancel'),
    eventComponent: 'Button',
  })
}

const onSaveName = async () => {
  gtEvent('clickEvent', {
    eventCategory: '클릭',
    eventAction: '닉네임 변경 > 저장 버튼 클릭',
    eventLabel: t('save'),
    eventSlot: '닉네임 변경',
    eventI18nAddr: useKoreanTranslation('save'),
    eventComponent: 'Button',
  })
  // 입력 한 닉네임이 있는지 확인
  const isEmptyNickname = useCheckEmpty(state.userNcnm)
  if (isEmptyNickname) {
    useToast(t('mypage.profile.placeholder.nickname'))
    state.userNcnm = ''
    return
  }
  // 입력한 닉네임에 특수문자 있는지 확인
  const isUnavailableNickname = useCheckUrlSpecialCharacter(state.userNcnm)
  if (isUnavailableNickname) {
    useToast(t('mypage.profile.toastMessage.unavailableNickname'))
    return
  }

  // 사용자 프로필 업데이트
  if (user.value?.cmtyUserSeCode === 'C') {
    try {
      const payload: CreatorProfileEditPayload = {
        proflDc: user.value!.proflDc,
        userNcnm: state.userNcnm,
        creatorLink: user.value!.creatorLink,
      }
      await creatorStore.editProfile(payload)
      await mypageStore.fetchProfile()
      state.isNickname = false
      gtEvent('userAction', {
        eventCategory: '사용자',
        eventAction: '닉네임 변경',
        eventLabel: t('save'),
        eventSlot: '닉네임 변경',
        eventI18nAddr: useKoreanTranslation('save'),
        eventComponent: 'Button',
        userNcnm: state.userNcnm,
      })
    } catch (err) {
      if (request.isAxiosError(err) && err.response?.status === 2010) {
        await showDialogModal(
          t('mypage.profile.dialog.title'),
          t('mypage.profile.dialog.doubleNickname')
        )
        state.userNcnm = ''
      } else {
        useToast(t('commonError.network.message'))
      }
    }
  } else {
    try {
      const payload: ProfilePayload = {
        proflDc: user.value!.proflDc,
        userNcnm: state.userNcnm,
      }
      await mypageStore.editProfile(payload)
      state.isNickname = false
      gtEvent('userAction', {
        eventCategory: '사용자',
        eventAction: '닉네임 변경',
        eventLabel: t('save'),
        eventSlot: '프로필 수정 > 닉네임 변경 > 저장',
        eventI18nAddr: useKoreanTranslation('save'),
        eventComponent: 'Button',
        userNcnm: state.userNcnm,
      })
      // 유저 홈에서 닉네임 변경이라면 라우터 리플레이스
      if (
        String(route.name).includes(ROUTES.USER.name) &&
        String(route.params.id) !== payload.userNcnm
      ) {
        const query = route.query
        const currentRoute = useGetRouteConst(String(route.name))
        if (currentRoute) {
          useNavigations({
            url: useRoutePathIdChange(currentRoute.path, {
              id: payload.userNcnm,
            }),
            query,
            type: 'replace',
          })
        }
      }
    } catch (err) {
      if (request.isAxiosError(err) && err.response?.status === 2010) {
        await showDialogModal(
          t('mypage.profile.dialog.title'),
          t('mypage.profile.dialog.doubleNickname')
        )
        state.userNcnm = ''
      } else {
        useToast(t('commonError.network.message'))
      }
    }
  }
}

const onCancelProflDc = () => {
  state.proflDc = ''
  state.isSimpleIntroduce = false
  gtEvent('clickEvent', {
    eventCategory: '클릭',
    eventAction: '한줄소개 변경 취소',
    eventLabel: t('cancel'),
    eventSlot: '프로필 수정 > 한줄소개 변경 > 취소',
    eventI18nAddr: useKoreanTranslation('cancel'),
    eventComponent: 'Button',
  })
}

const onSaveProflDc = async () => {
  gtEvent('clickEvent', {
    eventCategory: '클릭',
    eventAction: '프로필 수정 > 한줄소개 변경 > 저장 버튼 클릭',
    eventLabel: t('save'),
    eventSlot: '한줄소개 변경',
    eventI18nAddr: useKoreanTranslation('save'),
    eventComponent: 'Button',
  })

  // 입력한 닉네임에 특수문자 있는지 확인
  const isUnavailable = useCheckUrlSpecialCharacter(state.proflDc)
  if (isUnavailable) {
    useToast(t('mypage.profile.toastMessage.unavailableSimpleIntroduce'))
    return
  }

  // 사용자 프로필 업데이트
  if (user.value?.cmtyUserSeCode === 'C') {
    try {
      const payload: CreatorProfileEditPayload = {
        proflDc: state.proflDc,
        userNcnm: user.value!.userNcnm,
        creatorLink: user.value!.creatorLink,
      }
      await creatorStore.editProfile(payload)
      await mypageStore.fetchProfile()
      state.isSimpleIntroduce = false
      gtEvent('userAction', {
        eventCategory: '사용자',
        eventAction: '한줄소개 변경',
        eventLabel: t('save'),
        eventSlot: '프로필 수정 > 한줄소개 변경 > 저장',
        eventI18nAddr: useKoreanTranslation('save'),
        eventComponent: 'Button',
        userNcnm: state.proflDc,
      })
    } catch (err) {
      if (request.isAxiosError(err) && err.response?.status === 2010) {
        await showDialogModal(
          t('mypage.profile.dialog.title'),
          t('mypage.profile.dialog.doubleNickname')
        )
        state.userNcnm = ''
      } else {
        useToast(t('commonError.network.message'))
      }
    }
  } else {
    try {
      const payload: ProfilePayload = {
        proflDc: state.proflDc,
        userNcnm: user.value!.userNcnm,
      }
      await mypageStore.editProfile(payload)
      state.isSimpleIntroduce = false
      gtEvent('userAction', {
        eventCategory: '사용자',
        eventAction: '한줄 소개 변경',
        eventLabel: t('save'),
        eventSlot: '프로필 수정 > 닉네임 변경 > 저장',
        eventI18nAddr: useKoreanTranslation('save'),
        eventComponent: 'Button',
        userNcnm: state.userNcnm,
      })

      // 유저 홈에서 닉네임 변경이라면 라우터 리플레이스
      if (
        String(route.name).includes(ROUTES.USER.name) &&
        String(route.params.id) !== payload.userNcnm
      ) {
        const query = route.query
        const currentRoute = useGetRouteConst(String(route.name))
        if (currentRoute) {
          useNavigations({
            url: useRoutePathIdChange(currentRoute.path, {
              id: payload.userNcnm,
            }),
            query,
            type: 'replace',
          })
        }
      }
    } catch (err) {
      if (request.isAxiosError(err) && err.response?.status === 2010) {
        await showDialogModal(
          t('mypage.profile.dialog.title'),
          t('mypage.profile.dialog.doubleNickname')
        )
        state.userNcnm = ''
      } else {
        useToast(t('commonError.network.message'))
      }
    }
  }
}

const onDeleteProfile = async () => {
  gtEvent('clickEvent', {
    eventCategory: '클릭',
    eventAction: '프로필 사진 삭제 버튼 클릭',
    eventLabel: 'close-compact',
    eventSlot: '프로필 이미지 삭제 아이콘',
    eventI18nAddr: 'close-compact',
    eventComponent: 'Button',
  })
  await uploadImageDialogModal(
    t('mypage.profile.dialog.title'),
    t('mypage.profile.dialog.initImage')
  )
}

const showDialogModal = (title: string, content: string) => {
  return modalShow({
    component: NovaModalDialog,
    bind: {
      name: modalsName.MODAL_DIALOG,
      btns: [
        {
          label: t('confirm'),
          theme: 'primary-blue-dark',
          size: 32,
          onClick: async () => {
            await modalHide(modalsName.MODAL_DIALOG)
          },
        },
      ],
    },
    slots: {
      title,
      content,
    },
  })
}

const onModifyProfile = async () => {
  // 앱 일때 스토리지 권한 체크
  if (appStore.isApp) {
    const { data: permission } = await requestPermission('storage')

    if (permission.status === 'off') {
      const accessAble = await checkPermission({
        permission: 'storage',
        permissionType: 'profileImage',
      })
      if (!accessAble) return
    }
  }
  gtEvent('clickEvent', {
    eventCategory: '클릭',
    eventAction: '프로필 이미지 변경 버튼 클릭',
    eventLabel: '',
    eventSlot: '프로필 수정 > 프로필 이미지 변경',
    eventI18nAddr: '',
    eventComponent: 'Button',
  })
  try {
    const { formData } = await useGetFiles({
      accept: 'image/*',
      multiple: false,
      formDataName: 'profileImg',
    })

    const payload: CreatorProfileByCreatorLinkPayload = {
      creatorLink: user.value!.creatorLink,
    }
    await creatorStore.createProfilePhoto(formData, payload)
    gtEvent('fileAction', {
      eventCategory: '파일',
      eventAction: '프로필 이미지 파일 업로드',
      eventLabel: '',
      eventSlot: '프로필 수정 > 프로필 이미지 업로드',
      eventI18nAddr: '',
      eventComponent: 'File Upload',
      fileName: mypageStore.profile!.userProflUrl,
    })
    await mypageStore.fetchProfile()
    gtEvent('userAction', {
      eventCategory: '사용자',
      eventAction: '프로필 이미지 변경',
      eventLabel: '',
      eventSlot: '',
      eventI18nAddr: '',
      eventComponent: 'Button',
    })
  } catch (err) {
    if (request.isAxiosError(err) && err.response) {
      switch (err.response.status) {
        case 2005:
          useToast(t('mypage.profile.toastMessage.imgError2005'))
          break
        default:
          useToast(t('mypage.profile.toastMessage.imgError'))
          break
      }
    }
  }
}

// const onUploadBgImg = async () => {
//   // 앱 일때 스토리지 권한 체크
//   if (appStore.isApp) {
//     const { data: permission } = await requestPermission('storage')
//
//     if (permission.status === 'off') {
//       const accessAble = await checkPermission({
//         permission: 'storage',
//         permissionType: 'homeHeaderImage',
//       })
//       if (!accessAble) return
//     }
//   }
//   gtEvent('clickEvent', {
//     eventCategory: '클릭',
//     eventAction: '백그라운드 이미지 변경 버튼 클릭',
//     eventLabel: '',
//     eventSlot: '프로필 수정 > 백그라운드 이미지',
//     eventI18nAddr: '',
//     eventComponent: 'Button',
//   })
//   try {
//     const { formData } = await useGetFiles({
//       accept: 'image/*',
//       multiple: false,
//       formDataName: 'bgImg',
//     })
//
//     await mypageStore.regProfileBgImg(formData)
//     gtEvent('fileAction', {
//       eventCategory: '파일',
//       eventAction: '백그라운드 이미지 파일 업로드',
//       eventLabel: '',
//       eventSlot: '프로필 수정 > 백그라운드 이미지 업로드',
//       eventI18nAddr: '',
//       eventComponent: 'File Upload',
//       fileName: mypageStore.profile!.userProflUrl,
//     })
//     await mypageStore.fetchProfile()
//     gtEvent('userAction', {
//       eventCategory: '사용자',
//       eventAction: '백그라운드 이미지 변경',
//       eventLabel: '',
//       eventSlot: '백그라운드 이미지 변경',
//       eventI18nAddr: '',
//       eventComponent: '',
//     })
//   } catch (err) {
//     if (request.isAxiosError(err) && err.response) {
//       switch (err.response.status) {
//         case 2005:
//           useToast(t('mypage.profile.toastMessage.imgError2005'))
//           break
//         default:
//           useToast(t('mypage.profile.toastMessage.imgError'))
//           break
//       }
//     }
//   }
// }

const handleOnSignOff = async () => {
  // 탈퇴 가능 여부 조회
  await userStore.fetchCheckRetireType()
  // DAO 개설자 일떄
  if (
    userStore.retireType?.opening &&
    userStore.retireType?.daoSttusCode === 'A'
  ) {
    await modalShow({
      component: NovaModalDialog,
      bind: {
        name: modalsName.MODAL_DIALOG,
        btns: [
          {
            label: t('dao.leave.dialog.actions.checkBtn'),
            theme: 'primary-blue-dark',
            size: 32,
            onClick: async () => {
              await modalHide(modalsName.MODAL_DIALOG)
            },
          },
        ],
      },
      slots: {
        title: t('dao.leave.dialog.title'),
        content: t('dao.leave.dialog.message'),
      },
    })
    gtEvent('clickEvent', {
      eventCategory: '클릭',
      eventAction: '회원 탈퇴 신청 클릭',
      eventLabel: t('sign.off'),
      eventSlot: '유저 패널 > 설정 > 프로필 수정 > 회원 탈퇴',
      eventI18nAddr: useKoreanTranslation('sign.off'),
      eventComponent: 'Button',
    })
  } else {
    await modalShow({
      component: NovaModalCommon,
      bind: {
        name: modalsName.MODAL_MEMBERSHIP_OFF,
        showCloseBtn: true,
        mobileFull: true,
      },
      on: {
        close: async () => {
          gtEvent('clickEvent', {
            eventCategory: '클릭',
            eventAction: '회원 탈퇴 모달 > 닫기',
            eventLabel: t('membershipOff'),
            eventSlot: '회원 탈퇴',
            eventI18nAddr: useKoreanTranslation('membershipOff'),
            eventComponent: 'Button',
          })
          await modalHide(modalsName.MODAL_MEMBERSHIP_OFF)
        },
      },
      slots: {
        title: t('membershipOff'),
        noScrollContents: {
          component: NovaModalRetireCheckPassword,
          on: {
            close: async () => {
              gtEvent('clickEvent', {
                eventCategory: '클릭',
                eventAction: '회원 탈퇴 모달 > 닫기',
                eventLabel: t('membershipOff'),
                eventSlot: '회원 탈퇴',
                eventI18nAddr: useKoreanTranslation('membershipOff'),
                eventComponent: 'Button',
              })
              await modalHide(modalsName.MODAL_MEMBERSHIP_OFF)
            },
          },
        },
      },
    })
  }
}

const handleOnPasswordChangeProcess = () => {
  gtEvent('clickEvent', {
    eventCategory: '클릭',
    eventAction: '비밀번호 변경하기 버튼 클릭',
    eventLabel: t('changePassword.change'),
    eventSlot: '프로필 수정 > 비밀번호 변경',
    eventI18nAddr: useKoreanTranslation('changePassword.change'),
    eventComponent: 'Button',
  })
  openMembershipServicePopup.searchPW()
}

const closeAccountSetting = () => {
  layoutStore['layer:close'](LayerType.USER_PROFILE_EDIT_PANEL)
  gtEvent('clickEvent', {
    eventCategory: '클릭',
    eventAction: '프로필 수정 닫기 ',
    eventLabel: '',
    eventSlot: '헤더',
    eventI18nAddr: '',
    eventComponent: 'Button',
  })
}

const handleOnBgImageLoadSuccess = () => {
  isBgImgLoadSuccess.value = true
}

const uploadImageDialogModal = async (title: string, content: string) => {
  await modalShow({
    component: NovaModalDialog,
    bind: {
      name: modalsName.MODAL_UPLOAD_IMAGE,
      btns: [
        {
          label: t('cancel'),
          theme: 'transparent',
          size: 32,
          onClick: async () => {
            gtEvent('clickEvent', {
              eventCategory: '클릭',
              eventAction: '프로필 이미지 삭제 > 취소 버튼 클릭',
              eventLabel: t('cancel'),
              eventSlot: '프로필 이미지 삭제 모달',
              eventI18nAddr: useKoreanTranslation('cancel'),
              eventComponent: 'Button',
            })
            await modalHide(modalsName.MODAL_UPLOAD_IMAGE)
            return false
          },
        },
        {
          label: t('confirm'),
          theme: 'primary-blue-light',
          size: 32,
          onClick: async () => {
            gtEvent('clickEvent', {
              eventCategory: '클릭',
              eventAction: '프로필 이미지 삭제 > 확인 버튼 클릭',
              eventLabel: t('mypage.profile.button.save'),
              eventSlot: '프로필 삭제 모달',
              eventI18nAddr: useKoreanTranslation('mypage.profile.button.save'),
              eventComponent: 'Button',
            })
            try {
              await mypageStore.deleteProfilePhoto()
              await modalHide(modalsName.MODAL_UPLOAD_IMAGE)
            } catch (err: any) {
              const errorData = err.response
              if (errorData.status === 2004) {
                await showDialogModal(
                  t('mypage.profile.dialog.title'),
                  t('mypage.profile.dialog.noImage')
                )
              }
            }
          },
        },
      ],
    },
    slots: {
      title,
      content,
    },
  })
}
</script>

<template>
  <div
    :class="[
      'page-setting',
      'nova-box-account',
      { on: isOpen, ready: isReady },
    ]"
    :style="styles"
  >
    <div class="dim" @click="closeAccountSetting" />

    <div class="tab-box">
      <ul>
        <li class="active">
          <a>{{ t('accountInfo') }}</a>
        </li>
      </ul>
      <a class="btn-close-layer" @click="closeAccountSetting">닫기</a>
    </div>

    <div class="setting-boxes">
      <!-- 사용자 프로필 이미지 -->
      <div class="profile-image-wrap">
        <div
          :class="[
            'profile-image-box',
            { 'is-load-success': isBgImgLoadSuccess },
          ]"
        >
          <div class="image-container">
            <NovaImageContainer
              :key="mypageStore.profile?.backgroundImageUrl || v4()"
              :image-url="''"
              :empty-type="'default'"
              :ratio="'5:1'"
              @on-img-load="handleOnBgImageLoadSuccess"
            />
          </div>
          <NovaButtonIcon
            v-if="mypageStore.profile?.userAvataUseAt === 'N'"
            :theme="'transparent'"
            :icon="{ type: 'outline', name: 'close-compact' }"
            :size="24"
            class="btn-delete"
            @click="onDeleteProfile"
          />
          <div class="thumbnail">
            <NovaPortraitContainer
              :image-url="user?.userProflUrl"
              :size="'56'"
            />
            <button class="btn-img-setting" @click="onModifyProfile" />
          </div>
        </div>
      </div>

      <!-- 사용자 기본 프로필 -->
      <div class="setting-box setting-user-info">
        <ul>
          <!-- 이름 -->
          <li>
            <strong>{{ t('name') }}</strong>
            <span class="txt">
              {{ user?.userNm }}
            </span>
            <!--<NovaButtonText-->
            <!--  v-if="!kycCertified"-->
            <!--  :label="t('kyc.userKycInfo.certifiedAction.yet')"-->
            <!--  :theme="'primary-red-ultra-light'"-->
            <!--  :size="20"-->
            <!--  class="btn-kyc"-->
            <!--  @click="-->
            <!--    () => {-->
            <!--      certifiedKYC('reward')-->
            <!--    }-->
            <!--  "-->
            <!--/>-->
          </li>

          <!-- 닉네임 -->
          <li>
            <strong>{{ t('nickName') }}</strong>

            <div v-if="!state.isNickname" class="box-right">
              <span>{{ user?.userNcnm || '-' }}</span>
              <Tippy
                :append-to="'parent'"
                :content="t('modify')"
                :interactive="true"
                :theme="'simple'"
                :placement="'bottom'"
              >
                <NovaButtonIcon
                  :icon="{ type: 'outline', name: 'write' }"
                  :size="16"
                  @click="handleOnChangeUserNcnm"
                />
              </Tippy>
            </div>

            <!-- 수정 버튼 클릭시 아래 마크업 표출 -->
            <div v-if="state.isNickname" class="box-right-edit">
              <div class="input-box">
                <input v-model="state.userNcnm" type="text" />
                <span>
                  ({{ countLengthByUserNcnm }}/{{ state.nameMaxLength }})
                </span>
              </div>

              <div class="box-btn">
                <NovaButtonText
                  :size="32"
                  :label="t('cancel')"
                  @click="onCancelName"
                />

                <NovaButtonText
                  class="btn-save"
                  :size="32"
                  :label="t('save')"
                  @click="onSaveName"
                />
              </div>
            </div>
          </li>

          <!-- 한줄소개 -->
          <li>
            <strong>{{ t('simpleIntroduce') }}</strong>

            <div v-if="!state.isSimpleIntroduce" class="box-right">
              <span>{{ user?.proflDc || '-' }}</span>
              <Tippy
                :append-to="'parent'"
                :content="t('modify')"
                :interactive="true"
                :theme="'simple'"
                :placement="'bottom'"
              >
                <NovaButtonIcon
                  :icon="{ type: 'outline', name: 'write' }"
                  :size="16"
                  @click="handleOnChangeSimpleIntroduce"
                />
              </Tippy>
            </div>

            <!-- 수정 버튼 클릭시 아래 마크업 표출 -->
            <div v-if="state.isSimpleIntroduce" class="box-right-edit">
              <div class="input-box">
                <input v-model="state.proflDc" type="text" />
                <span>
                  ({{ counteLengthByProflDc }}/{{ state.proflDcMaxLength }})
                </span>
              </div>

              <div class="box-btn">
                <NovaButtonText
                  :size="32"
                  :label="t('cancel')"
                  @click="onCancelProflDc"
                />

                <NovaButtonText
                  class="btn-save"
                  :size="32"
                  :label="t('save')"
                  @click="onSaveProflDc"
                />
              </div>
            </div>
          </li>

          <!-- 이메일 -->
          <li>
            <strong>{{ t('email') }}</strong>
            <span>{{ user?.email }}</span>
          </li>

          <!-- 생년월일 -->
          <li>
            <strong>{{ t('birthday') }}</strong>
            <span class="txt">{{ user?.birth }}</span>
            <!--<NovaButtonText-->
            <!--  v-if="!kycCertified"-->
            <!--  :label="t('kyc.userKycInfo.certifiedAction.yet')"-->
            <!--  :theme="'primary-red-ultra-light'"-->
            <!--  :size="20"-->
            <!--  class="btn-kyc"-->
            <!--  @click="-->
            <!--    () => {-->
            <!--      // 2024.02.21 릴리즈에서 무조건 보상 문구로 변경됨-->
            <!--      // certifiedKYC('service')-->
            <!--      certifiedKYC('reward')-->
            <!--    }-->
            <!--  "-->
            <!--/>-->
          </li>

          <!-- 추천인 이메일 -->
          <li>
            <strong>{{ t('recommenderEmail') }}</strong>
            <span>{{ user?.recomendUserId || '-' }}</span>
          </li>

          <!-- 비밀번호 변경 -->
          <li>
            <strong>{{ t('changePassword.title') }}</strong>
            <span
              class="text-hyper-link"
              @click="handleOnPasswordChangeProcess"
            >
              {{ t('changePassword.change') }}
            </span>
          </li>
        </ul>
      </div>

      <!-- 크리에이터 소개 -->
      <NovaBoxAccountSectionCreatorIntro v-if="isCreator" />

      <!-- 크리에이터 카테고리 -->
      <NovaBoxAccountSectionCreatorCategory v-if="isCreator" />

      <!-- 링크트리 -->
      <NovaBoxAccountSectionCreatorLinkTree v-if="isCreator" />

      <!-- LMNOVA 링크 일반유저 숨김 -->
      <NovaBoxAccountSectionHomeLink v-if="isCreator" />

      <!-- 추천인 링크 -->
      <NovaBoxAccountSectionReferralLink />

      <!-- 휴대전화 번호 -->
      <NovaBoxAccountSectionPhoneNumber v-if="isCreator" />

      <!-- 계좌번호 -->
      <NovaBoxAccountSectionBankAccount v-if="user?.prmbrshAt === 'Y'" />

      <!-- 회원탈퇴 -->
      <div class="setting-box setting-withdraw">
        <div class="title">{{ t('membershipOff') }}</div>

        <div class="box-input">
          <div class="line">
            <span>{{ t('sign.off') }}</span>

            <NovaButtonText
              :label="t('apply')"
              :theme="'secondary-gray'"
              :size="28"
              class="btn-account"
              @click="handleOnSignOff"
            />
          </div>
        </div>
      </div>

      <!-- 로딩 인디케이터 -->
      <NovaLoadingIndicator
        v-if="!isReady"
        class="account-loading-indicator"
        :bg-bright="'light'"
      />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.page-setting {
  position: absolute;
  top: calc(74px + 16px);
  right: 16px;
  width: 400px;
  z-index: $z-index-user-panel;
  background: #f7f7f7;
  border: 1px solid #eaeaea;
  box-shadow: 1px 2px 9px rgba(0, 0, 0, 0.05);
  border-radius: $radius-10;
  overflow: hidden;
  pointer-events: none;
  opacity: 0;
  @include transition(top 0.2s ease-in-out, opacity 0.2s ease-in-out);

  &.on {
    top: 74px;
    opacity: 1;
    pointer-events: auto;

    &.ready {
      .setting-boxes {
        opacity: 1;
      }
    }
  }

  > .dim {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: hex-to-rgba($color-black, 0.65);
    opacity: 0;
    @include transition(opacity 0.2s ease-in-out);
    z-index: -1;
  }

  .tab-box {
    padding: 0 20px;
    background-color: #fff;
    position: relative;

    .btn-close-layer {
      display: block;
      width: 24px;
      height: 24px;
      background: url('assets/images/icon-modal-close.png') no-repeat center;
      background-size: 14px 14px;
      position: absolute;
      top: 50%;
      right: 20px;
      z-index: 10;
      transform: translateY(-50%);
      text-indent: -99999px;
      cursor: pointer;
    }

    ul {
      display: flex;
      align-items: center;

      li {
        a {
          display: block;
          position: relative;
          padding: 20px 0;
          font-size: 16px;
          font-weight: bold;
        }

        &.active {
          a {
            color: #347ae2;

            &:before {
              display: block;
              content: '';
              width: 100%;
              height: 2px;
              background-color: #347ae2;
              position: absolute;
              left: 0;
              bottom: 0;
            }

            @include mobile {
              color: $color-black;

              &:before {
                display: none;
              }
            }
          }
        }
      }
    }
  }

  .setting-boxes {
    position: relative;
    display: flex;
    height: calc(65vh - 70px);
    flex-direction: column;
    gap: 18px;
    padding: 28px 18px 24px 18px;
    overflow: hidden;
    overflow-y: auto;
    opacity: 0.6;
    transition: opacity 0.2s ease-in-out;

    .btn-edit {
      width: 16px;
      height: 16px;
      background-image: url('assets/images/icon-edit.png');
      background-size: 100% 100%;
      text-indent: -99999px;
    }

    .setting-box {
      background-color: #fff;
      border-radius: 10px;
      padding: 16px;

      .title {
        font-size: 15px;
        font-weight: bold;
        margin-bottom: 15px;
      }

      .btn-account {
        font-weight: 400;
      }
    }

    .setting-user-info {
      ul {
        display: flex;
        flex-direction: column;
        gap: 24px;
        font-size: 14px;

        li {
          display: flex;
          align-items: flex-start;
          justify-content: left;

          span {
            word-break: break-all;
            font-size: 14px;
            font-weight: 400;
            line-height: 19.6px;
            letter-spacing: -0.025em;
            text-align: left;
            color: $color-text-12;

            &.text-hyper-link {
              cursor: pointer;
              color: $color-primary;
            }
            &.txt {
              /*margin-right: 10px;*/
            }
          }

          strong {
            flex-shrink: 0;
            width: 114px;
            font-weight: 500;
            word-break: break-word;
            color: #111111;
          }

          .box-right {
            width: calc(100% - 90px);
            display: flex;
            justify-content: space-between;
            align-items: center;
          }

          .box-right-edit {
            flex-grow: 1;

            .input-box {
              border: 1px solid #ccc;
              height: 36px;
              padding: 0 5px;
              border-radius: 3px;
              display: flex;
              justify-content: space-between;
              align-items: center;

              input {
                width: 70%;
                height: 100%;
              }

              span {
                font-size: 11px;
              }
            }

            .box-btn {
              display: flex;
              align-items: center;
              justify-content: flex-end;
              gap: 10px;
              margin-top: 10px;

              button {
                height: 28px;
                padding: 0 10px;
                font-size: 13px;
                font-weight: bold;
                letter-spacing: -0.3px;
                border-radius: 5px;
                color: $color-99;
                background-color: #f6f6f6;

                &.btn-save {
                  background: #347ae2;
                  color: #fff;
                }

                &.btn-cancel {
                  background: #347ae2;
                  color: #fff;
                }
              }
            }
          }
        }
      }
    }

    .setting-link {
      .box-input {
        display: flex;
        align-items: center;
        gap: 5px;

        &.no-gap {
          gap: 0;
        }

        span {
          font-size: 13px;
          color: $color-99;
        }

        input {
          height: 28px;
          font-size: 13px;
          border: 1px solid #dddddd;
        }
      }
      .box-btn {
        display: flex;
        align-items: center;
        justify-content: flex-end;
        gap: 10px;
        margin-top: 20px;

        button {
          padding: 0 10px;
          min-width: 44px;
          height: 27px;
          font-weight: 400;
          font-size: 13px;
          line-height: 19px;
          letter-spacing: -0.05em;
          color: #3f4354;
          background: $color-bg-1;
          border-radius: 5px;
          text-align: center;

          &.btn-save {
            background: #347ae2;
            color: #fff;
          }
        }
      }
    }

    .setting-withdraw {
      .line {
        display: flex;
        justify-content: space-between;
        align-items: center;

        span {
          color: $color-99;
          font-size: 13px;
        }

        //button {
        //  flex-shrink: 0;
        //  width: auto;
        //  height: 27px;
        //  font-weight: 400;
        //  font-size: 13px;
        //  line-height: 19px;
        //  letter-spacing: -0.05em;
        //  color: #3f4354;
        //  background: $color-bg-1;
        //  border-radius: 5px;
        //  text-align: center;
        //}
      }
    }

    .account-loading-indicator {
      position: absolute;
      top: 0;
      left: 0;
      z-index: 10;
      width: 100%;
      height: 100%;
    }
  }
  .profile-image-wrap {
    border-radius: 16px 16px 0 0;
    background-color: white;

    .profile-image-box {
      display: flex;
      align-items: center;
      justify-content: center;
      position: relative;
      border-radius: 16px 16px 0 0;
      background-size: cover;
      background-position: center center;
      background-repeat: no-repeat;
      overflow: hidden;

      &.is-load-success {
        background: inherit;
      }

      .btn-box,
      .btn-delete {
        position: absolute;
        right: 0;
        bottom: 0;
      }

      .btn-delete {
        color: $color-white;
        top: 0;
        bottom: auto;
        top: calc(50% - 35px); /* 자식 1의 높이에서 15px 위로 이동 */
        left: calc(50% + 23px); /* 자식 1의 너비에서 18px 왼쪽으로 이동 */
      }

      .btn-box {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 30px;
        height: 30px;

        .input-file {
          width: 0;
          height: 0;
          visibility: hidden;
        }
      }
      .image-container {
        width: 100%;
        height: 100%;
      }

      .thumbnail {
        position: absolute;
        top: 0;
        left: 0;
        width: 56px;
        height: 56px;
        top: 50%; /* 수직 중앙 */
        left: 50%; /* 수평 중앙 */
        transform: translate(-50%, -50%); /* 정확한 중앙 정렬 */

        .btn-img-setting {
          top: 50%;
          right: 50%;
          transform: translate(50%, -50%);
        }
      }

      .btn-img-setting {
        position: absolute;
        top: 16px;
        right: 16px;
        text-indent: -99999px;
        width: 24px;
        height: 24px;
        background-image: url('assets/images/icon-camera.png');
        background-size: 100% 100%;
        z-index: 10;
      }
    }
  }

  @include mobile {
    width: 100%;
    position: fixed;
    top: 0;
    transform: translateY(0);
    border-radius: 0;
    display: flex;
    flex-direction: column;

    &.on {
      top: 0;
      left: 0;
    }

    .setting-boxes {
      flex-grow: 1;
    }
  }
}
</style>
